/**
 * Production-grade reCAPTCHA v3 utility service
 * Handles token generation, caching, retry logic, and error handling
 */

import { config } from '@/config';
import {
  RecaptchaAction,
  RecaptchaError,
  RecaptchaErrorCode,
  RecaptchaTokenResponse,
} from '@/types/recaptcha';

/**
 * Configuration for reCAPTCHA service
 */
const RECAPTCHA_CONFIG = {
  // Token validity duration (2 minutes as per Google's recommendation)
  TOKEN_VALIDITY_MS: 2 * 60 * 1000,
  // Maximum time to wait for reCAPTCHA to load
  LOAD_TIMEOUT_MS: 10000,
  // Maximum time to wait for token execution
  EXECUTION_TIMEOUT_MS: 5000,
  // Maximum retry attempts for token generation
  MAX_RETRY_ATTEMPTS: 3,
  // Delay between retry attempts (exponential backoff)
  RETRY_DELAY_MS: 1000,
} as const;

/**
 * Token cache to avoid unnecessary reCAPTCHA executions
 */
interface CachedToken {
  token: string;
  action: RecaptchaAction;
  timestamp: number;
  expiresAt: number;
}

class RecaptchaTokenCache {
  private cache = new Map<RecaptchaAction, CachedToken>();

  set(action: RecaptchaAction, token: string): void {
    const timestamp = Date.now();
    this.cache.set(action, {
      token,
      action,
      timestamp,
      expiresAt: timestamp + RECAPTCHA_CONFIG.TOKEN_VALIDITY_MS,
    });
  }

  get(action: RecaptchaAction): string | null {
    const cached = this.cache.get(action);
    if (!cached) return null;

    // Check if token is still valid (with 10 second buffer)
    if (Date.now() >= cached.expiresAt - 10000) {
      this.cache.delete(action);
      return null;
    }

    return cached.token;
  }

  clear(action?: RecaptchaAction): void {
    if (action) {
      this.cache.delete(action);
    } else {
      this.cache.clear();
    }
  }

  // Clean up expired tokens periodically
  cleanupExpired(): void {
    const now = Date.now();
    for (const [action, cached] of this.cache.entries()) {
      if (now >= cached.expiresAt) {
        this.cache.delete(action);
      }
    }
  }
}

const tokenCache = new RecaptchaTokenCache();

// Periodically clean up expired tokens (every 5 minutes)
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      tokenCache.cleanupExpired();
    },
    5 * 60 * 1000
  );
}

/**
 * Check if reCAPTCHA is configured and enabled
 */
export function isRecaptchaEnabled(): boolean {
  return config.recaptchaEnabled && !!config.recaptchaPublicKey;
}

/**
 * Get the reCAPTCHA site key
 */
export function getRecaptchaPublicKey(): string | null {
  return config.recaptchaPublicKey;
}

/**
 * Wait for reCAPTCHA to be ready with timeout
 */
async function waitForRecaptchaReady(
  timeoutMs: number = RECAPTCHA_CONFIG.LOAD_TIMEOUT_MS
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(
        new RecaptchaError(
          'reCAPTCHA failed to load within timeout period',
          RecaptchaErrorCode.TIMEOUT
        )
      );
    }, timeoutMs);

    if (typeof window === 'undefined') {
      clearTimeout(timeout);
      reject(
        new RecaptchaError(
          'reCAPTCHA cannot be used in server-side context',
          RecaptchaErrorCode.NOT_LOADED
        )
      );
      return;
    }

    if (!window.grecaptcha) {
      clearTimeout(timeout);
      reject(
        new RecaptchaError(
          'reCAPTCHA script not loaded',
          RecaptchaErrorCode.NOT_LOADED
        )
      );
      return;
    }

    try {
      // Support both standard and enterprise reCAPTCHA
      const recaptcha = window.grecaptcha.enterprise || window.grecaptcha;
      recaptcha.ready(() => {
        clearTimeout(timeout);
        resolve();
      });
    } catch (error) {
      clearTimeout(timeout);
      reject(
        new RecaptchaError(
          'Failed to initialize reCAPTCHA',
          RecaptchaErrorCode.NOT_LOADED,
          error as Error
        )
      );
    }
  });
}

/**
 * Execute reCAPTCHA with timeout
 */
async function executeRecaptchaWithTimeout(
  siteKey: string,
  action: RecaptchaAction,
  timeoutMs: number = RECAPTCHA_CONFIG.EXECUTION_TIMEOUT_MS
): Promise<string> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(
        new RecaptchaError(
          `reCAPTCHA execution timed out for action: ${action}`,
          RecaptchaErrorCode.TIMEOUT
        )
      );
    }, timeoutMs);

    try {
      // Support both standard and enterprise reCAPTCHA
      const recaptcha = window.grecaptcha!.enterprise || window.grecaptcha!;
      recaptcha
        .execute(siteKey, { action })
        .then((token) => {
          clearTimeout(timeout);
          resolve(token);
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(
            new RecaptchaError(
              `reCAPTCHA execution failed for action: ${action}`,
              RecaptchaErrorCode.EXECUTION_FAILED,
              error
            )
          );
        });
    } catch (error) {
      clearTimeout(timeout);
      reject(
        new RecaptchaError(
          `Failed to execute reCAPTCHA for action: ${action}`,
          RecaptchaErrorCode.EXECUTION_FAILED,
          error as Error
        )
      );
    }
  });
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate reCAPTCHA token with retry logic
 * @param action - The action to execute (e.g., 'agent_session_create')
 * @param options - Optional configuration
 * @returns Promise resolving to reCAPTCHA token response
 */
export async function generateRecaptchaToken(
  action: RecaptchaAction,
  options: {
    useCache?: boolean;
    maxRetries?: number;
    retryDelay?: number;
  } = {}
): Promise<RecaptchaTokenResponse> {
  const {
    useCache = true,
    maxRetries = RECAPTCHA_CONFIG.MAX_RETRY_ATTEMPTS,
    retryDelay = RECAPTCHA_CONFIG.RETRY_DELAY_MS,
  } = options;

  // Check if reCAPTCHA is configured
  const siteKey = getRecaptchaPublicKey();
  if (!siteKey) {
    throw new RecaptchaError(
      'reCAPTCHA is not configured. Please set RECAPTCHA_SITE_KEY environment variable.',
      RecaptchaErrorCode.NOT_CONFIGURED
    );
  }

  // Check cache first if enabled
  if (useCache) {
    const cachedToken = tokenCache.get(action);
    if (cachedToken) {
      return {
        token: cachedToken,
        action,
        timestamp: Date.now(),
      };
    }
  }

  let lastError: RecaptchaError | null = null;

  // Retry logic with exponential backoff
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Wait for reCAPTCHA to be ready
      await waitForRecaptchaReady();

      // Execute reCAPTCHA
      const token = await executeRecaptchaWithTimeout(siteKey, action);

      // Cache the token
      if (useCache) {
        tokenCache.set(action, token);
      }

      return {
        token,
        action,
        timestamp: Date.now(),
      };
    } catch (error) {
      lastError =
        error instanceof RecaptchaError
          ? error
          : new RecaptchaError(
              'Unknown error during reCAPTCHA token generation',
              RecaptchaErrorCode.EXECUTION_FAILED,
              error as Error
            );

      // Don't retry for configuration or load errors
      if (
        lastError.code === RecaptchaErrorCode.NOT_CONFIGURED ||
        lastError.code === RecaptchaErrorCode.NOT_LOADED
      ) {
        throw lastError;
      }

      // Exponential backoff for retry
      if (attempt < maxRetries - 1) {
        await sleep(retryDelay * Math.pow(2, attempt));
      }
    }
  }

  // All retries failed
  throw (
    lastError ||
    new RecaptchaError(
      'Failed to generate reCAPTCHA token after all retry attempts',
      RecaptchaErrorCode.EXECUTION_FAILED
    )
  );
}

/**
 * Generate reCAPTCHA token or return null if not configured
 * This is a safe version that doesn't throw errors when reCAPTCHA is not configured
 * @param action - The action to execute
 * @param options - Optional configuration
 * @returns Promise resolving to token or null
 */
export async function generateRecaptchaTokenSafe(
  action: RecaptchaAction,
  options?: {
    useCache?: boolean;
    maxRetries?: number;
    retryDelay?: number;
  }
): Promise<string | null> {
  try {
    if (!isRecaptchaEnabled()) {
      return null;
    }
    const response = await generateRecaptchaToken(action, options);
    return response.token;
  } catch {
    return null;
  }
}

/**
 * Clear cached tokens
 * @param action - Optional specific action to clear, or clear all if not provided
 */
export function clearRecaptchaCache(action?: RecaptchaAction): void {
  tokenCache.clear(action);
}

/**
 * Preload reCAPTCHA for better performance
 * Call this early in the application lifecycle
 */
export async function preloadRecaptcha(): Promise<void> {
  if (!isRecaptchaEnabled()) {
    return;
  }

  try {
    await waitForRecaptchaReady();
  } catch {
    // Silent fail if preload fails
  }
}

/**
 * Get reCAPTCHA script URL
 */
export function getRecaptchaScriptUrl(): string | null {
  const siteKey = getRecaptchaPublicKey();
  if (!siteKey) return null;

  // Use enterprise version if needed, otherwise standard
  return `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&badge=bottomleft`;
}
